<template>
    <pt-dialog
        custom-class="heatmap-code-dialog"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        width="80%"
        title="Heatmap Code"
        :model-value="props.visible"
        @close="handleClose"
    >
        <!--应用范围-->
        <div :class="$style.pageRange">
            <pt-radio v-model="effectiveRange" label="site">Site</pt-radio>
            <pt-radio v-model="effectiveRange" label="regexp">Regexp</pt-radio>
            <pt-input
                v-model="regexp"
                :disabled="effectiveRange === 'site'"
                :class="$style.input"
            ></pt-input>
        </div>

        <div :class="$style.code">
            <pt-tabs v-model="activeTabKey" :visible-content="false">
                <pt-tab-pane
                    v-for="tab in tabOptions"
                    :name="tab.key"
                    :label="tab.label"
                    :key="tab.key"
                ></pt-tab-pane>
            </pt-tabs>
            <div :class="$style.codeMirror" v-if="activeTab">
                <Code
                    v-if="!queryLoading"
                    :code="activeTab.text"
                    :mode="activeTab.mode"
                    @change="handleCodeValueChange"
                ></Code>
            </div>
        </div>
        <template #footer>
            <pt-button type="outline" color="black" @click="handleClose">
                {{ $t('common.button_cancel') }}
            </pt-button>
            <pt-button
                type="contain"
                color="green"
                :class="$style.confirm"
                @click="handleConfirm"
                :disabled="saveLoading"
            >
                {{ $t('common.button_confirm') }}
            </pt-button>
        </template>
    </pt-dialog>
</template>

<script lang="ts" setup>
import Code from './Code.vue';
import { ref, onMounted, reactive, nextTick, watch, computed } from 'vue';
import {
    getHeatmapCode,
    saveHeatmapCode,
    EffectiveRangeType,
    UpdateHeatMapSpecialCorrespondenceInput
} from '@models/insight/useHeatmapCode';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import ptMessage from '@ui/message';

// interface
interface Props {
    visible: boolean;
    currentUrl: string;
}
// props
const props = defineProps<Props>();
// emits
const emit = defineEmits(['close']);
// state
const { profileInfo } = useProfileInfo();
const effectiveRange = ref<EffectiveRangeType>('site');
const regexp = ref('');
const codeId = ref('');
const activeTabKey = ref('css');
const saveLoading = ref(false);
const queryLoading = ref(true);
const tabOptions = ref([
    {
        key: 'css',
        mode: 'text/css',
        label: 'CSS',
        text: ''
    },
    {
        key: 'js',
        mode: 'javascript',
        label: 'Javascript',
        text: ''
    }
]);

const activeTab = ref(tabOptions.value.find(item => item.key === activeTabKey.value));

// watch
watch(
    () => [activeTabKey.value, tabOptions.value],
    () => {
        activeTab.value = tabOptions.value?.find(item => item.key === activeTabKey.value);
    },
    {
        deep: true
    }
);
// onMounted
onMounted(async () => {
    if (props.currentUrl && profileInfo.value?.sid) {
        await getHeatmapCode(
            {
                sid: profileInfo.value?.sid,
                url: props.currentUrl
            },
            res => {
                queryLoading.value = false;
                if (res && res[0]) {
                    const {
                        effectiveRange: range,
                        cssContent,
                        jsContent,
                        regexp: reg,
                        id
                    } = res[0];
                    effectiveRange.value = range;
                    regexp.value = reg;
                    codeId.value = id;
                    tabOptions.value = tabOptions.value.map(item => {
                        if (item.key === 'css') {
                            item.text = cssContent;
                        } else if (item.key === 'js') {
                            item.text = jsContent;
                        }
                        return item;
                    });
                }
            },
            err => {
                queryLoading.value = false;
                console.log(err);
            }
        );
    }
});

/**
 * 关闭工具弹框
 */
function handleClose() {
    emit('close');
}

/**
 * @params value 更新后的code的内容
 */
function handleCodeValueChange(value: string) {
    activeTab.value && (activeTab.value.text = value);
}

/**
 * 确认提交更改的code
 */
async function handleConfirm() {
    // 无sid时报错
    if (!profileInfo.value?.sid) {
        ptMessage.error('Sid is required!');
        return;
    }
    // cssContent & jsContent都为空时报错
    if (tabOptions.value?.every(item => item.text?.trim() === '')) {
        ptMessage.error('css or javascript is both empty!');
        return;
    }
    // 当effectiveRange为正则且没有输入正则内容时报错
    if (effectiveRange.value === 'regexp' && !regexp.value?.trim()) {
        ptMessage.error('Please enter the regexp');
        return;
    }

    const cssContent = tabOptions.value?.find(item => item.key === 'css')?.text || '';
    const jsContent = tabOptions.value?.find(item => item.key === 'js')?.text || '';
    saveLoading.value = true;
    const params: UpdateHeatMapSpecialCorrespondenceInput = {
        sid: profileInfo.value?.sid,
        cssContent,
        jsContent,
        effectiveRange: effectiveRange.value,
        regexp: effectiveRange.value === 'regexp' ? regexp.value : ''
    };
    if (codeId.value) {
        // 有codeId 则为修改 否则为新增
        params.id = codeId.value;
    }
    const res = await saveHeatmapCode(params);
    saveLoading.value = false;
    if (res) {
        ptMessage.info('Save Successfully');
        emit('close');
    } else {
        ptMessage.info('Something went wrong, please save again.');
    }
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
:global {
    .heatmap-code-dialog {
        min-width: 800px;
        min-height: 80vh;
        .pt-dialog__footer {
            border-top: 1px solid $pt-black-40;
        }
    }
}
.codeMirror {
    min-height: 300px;
}
.confirm {
    margin-left: 20px;
}
.pageRange {
    margin-bottom: 10px;
    :global {
        .pt-radio {
            display: block;
            margin-bottom: 10px;
        }
    }
    .title {
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 10px;
    }
    .input {
        margin-left: 20px;
    }
}
</style>
